import React from 'react';
import AppHeader from '../components/layout/AppHeader';
import { motion } from 'framer-motion';
import { FiBriefcase, FiTool, FiActivity, FiUsers, FiCpu, FiGlobe, FiShield, FiTrendingUp, FiZap } from 'react-icons/fi';

const AboutUs: React.FC = () => {
    return (
        <div className="min-h-screen bg-premium-wood-dark text-white font-sans selection:bg-premium-gold/30">
            <AppHeader title="The TechUnity Vision" />

            {/* Hero Section: The Genesis */}
            <section className="relative pt-24 pb-32 overflow-hidden">
                <div
                    className="absolute inset-0 z-0 opacity-20"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2669&auto=format&fit=crop')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'grayscale(100%) contrast(1.2)'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-premium-wood-dark/50 via-premium-wood-dark/95 to-premium-wood-dark z-0" />

                <div className="container mx-auto px-4 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-4xl mx-auto"
                    >
                        <span className="text-premium-gold font-serif italic text-lg mb-4 block">The Genesis of a Revolution</span>
                        <h1 className="text-5xl md:text-7xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold-light via-premium-gold to-premium-gold-dark mb-8 leading-tight">
                            Handing Corporate Power <br /> to the Individual
                        </h1>
                        <p className="text-xl md:text-2xl text-white/90 font-light leading-relaxed mb-12 border-l-4 border-premium-gold/30 pl-8 text-left italic">
                            TechUnity Global was founded by <span className="text-premium-gold font-bold">Shawn Mutogo, the Founder/CEO</span>, with a singular, profound mission: to break the systemic cycle of unemployment by handing the infrastructure of a major corporation directly to the individual.
                        </p>
                    </motion.div>
                </div>
            </section>

            <main className="container mx-auto px-4 space-y-32 pb-32">

                {/* Section 1: The Virtual Company Concept */}
                <section className="grid lg:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl font-serif font-bold text-premium-gold mb-8 italic">Not Just an App—A Digital Sanctuary</h2>
                        <div className="space-y-6 text-white/80 text-lg leading-relaxed">
                            <p>
                                TechUnity Global is a revolutionary "Virtual Company" platform. It operates on the belief that the problem isn't a lack of talent in the workforce, but a lack of access to the professional environment needed for independent success.
                            </p>
                            <p>
                                Unlike typical freelance marketplaces like Upwork or Fiverr which treat professionals as commodities for one-off tasks, TechUnity Global serves as a premium <span className="text-premium-gold font-bold">"Operating System" for your career</span>.
                            </p>
                            <div className="bg-white/5 border border-white/10 p-6 rounded-2xl italic">
                                "TechUnity Global isn't about finding jobs. It's about becoming the job."
                            </div>
                        </div>
                    </motion.div>
                    <div className="relative">
                        <div className="absolute -inset-4 bg-premium-gold/20 blur-3xl rounded-full opacity-30 animate-pulse" />
                        <div className="relative bg-black/40 border border-white/10 rounded-3xl p-8 backdrop-blur-xl">
                            <div className="grid grid-cols-2 gap-6">
                                <FeatureCard icon={<FiBriefcase />} title="CEO of Self" desc="Empowering you to employ yourself." color="#D4AF37" />
                                <FeatureCard icon={<FiGlobe />} title="No Borders" desc="Geography stops limiting opportunity." color="#4A90E2" />
                                <FeatureCard icon={<FiUsers />} title="Team Power" desc="Form teams and build legacy." color="#50E3C2" />
                                <FeatureCard icon={<FiShield />} title="Structure" desc="Access Fortune 500 infrastructure." color="#9013FE" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 2: The Integrated Ecosystem */}
                <section className="bg-white/[0.02] border-y border-white/5 py-24 -mx-4 px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-20">
                            <h2 className="text-4xl font-serif font-bold mb-4">The Integrated Ecosystem</h2>
                            <p className="text-white/60 max-w-2xl mx-auto">
                                We don't just connect you to work; we provide the tools to execute it at a world-class level.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            <EcosystemCard
                                icon={<FiCpu />}
                                title="Virtual Workspaces"
                                items={['3D Immersive Rooms', 'Creative Whiteboards', 'Live Doc Editors', 'Real-time Conferencing']}
                            />
                            <EcosystemCard
                                icon={<FiTool />}
                                title="Pro Execution"
                                items={['In-browser IDE', 'Cloud Terminal', 'Dev Environments', 'Asset Management']}
                            />
                            <EcosystemCard
                                icon={<FiActivity />}
                                title="Business OS"
                                items={['HR & Payroll Module', 'Client Portal', 'Global Compliance', 'Automated Payments']}
                            />
                        </div>
                    </div>
                </section>

                {/* Section 3: Targeted Impact */}
                <section className="max-w-5xl mx-auto space-y-20">
                    <div className="text-center">
                        <h2 className="text-4xl font-serif font-bold text-premium-gold mb-6">Democratizing Global Opportunity</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        <div className="p-8 bg-black/40 border border-premium-gold/20 rounded-3xl">
                            <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                                <span className="p-2 bg-premium-gold/20 rounded-lg"><FiTrendingUp className="text-premium-gold" /></span>
                                For Unemployed Youth
                            </h3>
                            <p className="text-white/70 leading-relaxed mb-6 italic text-sm">
                                "Unemployment isn’t always about lack of skill. It’s often about lack of structure, access, and visibility."
                            </p>
                            <ul className="space-y-3 text-white/60 text-sm">
                                <li className="flex items-center gap-2"><FiZap className="text-premium-gold" /> Visibility for your hidden talents</li>
                                <li className="flex items-center gap-2"><FiZap className="text-premium-gold" /> Low-risk way to grow beyond a salary</li>
                                <li className="flex items-center gap-2"><FiZap className="text-premium-gold" /> Creating income together through teams</li>
                            </ul>
                        </div>

                        <div className="p-8 bg-black/40 border border-white/10 rounded-3xl">
                            <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                                <span className="p-2 bg-blue-500/20 rounded-lg"><FiBriefcase className="text-blue-400" /></span>
                                For The Employed
                            </h3>
                            <p className="text-white/70 leading-relaxed mb-6 italic text-sm">
                                "You can run a side business and build something sustainable without quitting your job."
                            </p>
                            <ul className="space-y-3 text-white/60 text-sm">
                                <li className="flex items-center gap-2"><FiZap className="text-blue-400" /> Ownership vs mere stability</li>
                                <li className="flex items-center gap-2"><FiZap className="text-blue-400" /> Collaborate globally after hours</li>
                                <li className="flex items-center gap-2"><FiZap className="text-blue-400" /> A platform that adapts to any field</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* CTA / Closing */}
                <section className="text-center py-20 relative overflow-hidden rounded-3xl">
                    <div className="absolute inset-0 bg-premium-gold/5 blur-3xl" />
                    <div className="relative z-10">
                        <h2 className="text-4xl md:text-5xl font-serif font-bold text-premium-gold mb-8">
                            Stop Waiting for Permission. <br /> Start Building Your Legacy.
                        </h2>
                        <p className="text-xl text-white/80 max-w-2xl mx-auto mb-12">
                            “TechUnity Global gives you a company before you can afford one.”
                        </p>
                        <div className="flex flex-wrap justify-center gap-6">
                            <button className="px-10 py-4 bg-premium-gold text-premium-wood-dark font-bold text-lg rounded-xl shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:shadow-[0_0_50px_rgba(212,175,55,0.6)] hover:scale-105 transition-all">
                                Join the Movement
                            </button>
                            <button className="px-10 py-4 border border-premium-gold text-premium-gold font-bold text-lg rounded-xl hover:bg-premium-gold/10 transition-all">
                                Contact Support
                            </button>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

/* Helper Components */
const FeatureCard = ({ icon, title, desc, color }: any) => (
    <div className="p-4 bg-white/5 border border-white/5 rounded-2xl hover:border-white/20 transition-all group">
        <div className="w-10 h-10 rounded-lg mb-3 flex items-center justify-center text-xl transition-all" style={{ backgroundColor: `${color}20`, color }}>
            {icon}
        </div>
        <h4 className="font-bold text-white text-sm mb-1">{title}</h4>
        <p className="text-[11px] text-white/40 leading-tight">{desc}</p>
    </div>
);

const EcosystemCard = ({ icon, title, items }: any) => (
    <motion.div
        whileHover={{ y: -5 }}
        className="p-8 bg-black/40 border border-white/10 rounded-3xl hover:border-premium-gold/50 transition-all"
    >
        <div className="text-3xl text-premium-gold mb-6">{icon}</div>
        <h3 className="text-xl font-bold text-white mb-6 font-serif">{title}</h3>
        <ul className="space-y-3">
            {items.map((item: string, i: number) => (
                <li key={i} className="flex items-center gap-3 text-white/50 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-premium-gold/40" />
                    {item}
                </li>
            ))}
        </ul>
    </motion.div>
);

export default AboutUs;
